import React, { useEffect, useMemo, useState } from "react";
import { StyleProvider } from "@ant-design/cssinjs";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { useTheme } from "./config/themeConfig";
import { store } from "./store";
import ConfigProvider from "antd/es/config-provider";
import AppContent from "./AppContent";
import enUS from "antd/locale/en_US";
import "antd/dist/reset.css";
import "./assets/styles/theme.css";
import "./assets/styles/antdTheme.css";
import "./assets/styles/kcc.css";
import "./assets/styles/kcc-theme.css";
import "./App.css";
import { injectStore } from "./services/axiosInstance";
import { environment } from "./config/environment";
import { isCognitoLoginAllowed, isOnlyIdmAllowed } from "./config/loginMethods";
import { resolveCognitoAutoLogin, goToLoginPicker } from "./services/cognitoAutoLogin";
import AuthScreen from "./components/AutenticatingComp";
import AuthorizationGate from "./components/AuthorizationGate";
import Login from "./pages/Login";

const localeLoaders: Record<string, () => Promise<{ default: any }>> = {
	en: () => Promise.resolve({ default: enUS }),
	hi: () => import(/* webpackChunkName: "antd-locale-hi" */ "antd/locale/hi_IN"),
	ja: () => import(/* webpackChunkName: "antd-locale-ja" */ "antd/locale/ja_JP"),
	zh: () => import(/* webpackChunkName: "antd-locale-zh" */ "antd/locale/zh_CN"),
};

injectStore(store);

// Pure-IDM tenants never render the in-app picker; any hit on /login is
// short-circuited to the legacy IDM redirect so deep-links and stale tabs
// resolve to the same flow as a non-Cognito build.
const IdmOnlyLoginRedirect: React.FC = () => {
	useEffect(() => {
		const target = environment.REACT_APP_REDIRECT_URL;
		if (target) window.location.replace(target);
	}, []);
	return null;
};

// Gate for the catch-all (app) route. On Cognito-enabled builds, any
// unauthenticated entry on ANY route auto-kicks the Cognito Client flow
// (full-page redirect to the Hosted UI) instead of showing the picker first.
//   - Existing session → render the app immediately.
//   - No session, first attempt → redirect to Hosted UI (shows the loader).
//   - No session, already attempted (bounced back unauthed) or redirect failed
//     → fall back to the in-app /login picker.
// Non-Cognito builds skip all of this and render the app directly (the axios
// 401 interceptor / route guards drive IDM + Entra as before).
const CognitoAutoLoginGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [status, setStatus] = useState<"checking" | "ready">(isCognitoLoginAllowed ? "checking" : "ready");

	useEffect(() => {
		if (!isCognitoLoginAllowed) return;
		let cancelled = false;
		(async () => {
			const outcome = await resolveCognitoAutoLogin();
			if (cancelled) return;
			if (outcome === "authenticated") {
				setStatus("ready");
			} else if (outcome === "show-picker") {
				goToLoginPicker();
			}
			// "redirecting" — the tab is navigating to the Hosted UI; keep the loader.
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	if (status === "checking") return <AuthScreen profile={true} />;
	return <>{children}</>;
};

const App: React.FC = () => {
	const theme = useTheme();
	const [locale, setLocale] = useState(enUS);
	const [language, setLanguage] = useState<string>(localStorage.getItem("i18nextLng") || "en");

	const updateLocale = () => {
		const storedLanguage = localStorage.getItem("i18nextLng") || "en";
		setLanguage(storedLanguage);
		const loader = localeLoaders[storedLanguage] || localeLoaders.en;
		loader()
			.then((mod) => setLocale(mod.default))
			.catch(() => setLocale(enUS));
	};

	useEffect(() => {
		updateLocale();
	}, [language]);

	useEffect(() => {
		const originalSetItem = localStorage.setItem;
		localStorage.setItem = function (key, value) {
			originalSetItem.call(this, key, value);
			if (key === "i18nextLng") {
				updateLocale();
			}
		};

		return () => {
			localStorage.setItem = originalSetItem;
		};
	}, []);

	const router = useMemo(
		() =>
			createBrowserRouter(
				[
					{
						path: "/login",
						element: isOnlyIdmAllowed ? <IdmOnlyLoginRedirect /> : <Login />,
					},
					{
						path: "*",
						element: (
							<CognitoAutoLoginGate>
								<AuthorizationGate>
									<AppContent />
								</AuthorizationGate>
							</CognitoAutoLoginGate>
						),
					},
				],
				{
					basename: environment.REACT_APP_BASE_PATH || "/",
					future: {
						v7_relativeSplatPath: true,
						v7_startTransition: true,
						v7_fetcherPersist: true,
						v7_normalizeFormMethod: true,
						v7_partialHydration: true,
						v7_skipActionErrorRevalidation: true,
					},
				},
			),
		[],
	);

	return (
		<ConfigProvider
			theme={{ token: theme }}
			locale={locale}
			getPopupContainer={(triggerNode) => {
				// If the trigger lives inside a scale container, render the popup inside
				// that same container so it inherits the transform and scales correctly.
				// Otherwise (navbar triggers, modals, etc.) fall back to document.body so
				// the popup is NOT accidentally injected into a scale container or app-content.
				// Navbar body-level popups are scaled separately via CSS in TopNavbar/style.css.
				if (triggerNode) {
					const scaled = triggerNode.closest(".scale-baseline-100, .scale-baseline-80") as HTMLElement;
					if (scaled) return scaled;
				}
				return document.body;
			}}>
			{" "}
			<StyleProvider>
				<RouterProvider router={router} />
			</StyleProvider>
		</ConfigProvider>
	);
};

export default App;
